import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle2, Download, Mail, Phone } from "lucide-react";
import { TenantHeader } from "@/components/layout/TenantHeader";
import TenantFooter from "@/components/layout/TenantFooter";

const QuotePrice = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { aigQuote, formData } = location.state || {};

  if (!aigQuote || !aigQuote.ok) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <TenantHeader />
        <div className="container mx-auto px-4 py-16">
          <Card className="max-w-2xl mx-auto">
            <CardContent className="p-8 text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Quote Not Available</h2>
              <p className="text-gray-600 mb-6">We couldn't retrieve your quote. Please try again.</p>
              <Button onClick={() => navigate("/")}>Return Home</Button>
            </CardContent>
          </Card>
        </div>
        <TenantFooter />
      </div>
    );
  }

  const premium = aigQuote.premium || "Contact us for pricing";
  const quoteNumber = aigQuote.quoteNumber || "N/A";

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <TenantHeader />
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-3xl mx-auto">
          {/* Success header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-4">
              <CheckCircle2 className="w-12 h-12 text-green-600" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Your Quote is Ready!</h1>
            <p className="text-lg text-gray-600">Here's your instant insurance quote</p>
          </div>

          {/* Price card */}
          <Card className="mb-6 border-2 border-blue-200 shadow-xl">
            <CardContent className="p-8">
              <div className="text-center mb-6">
                <p className="text-sm text-gray-600 mb-2">Annual Premium</p>
                <div className="text-5xl font-bold text-blue-600 mb-2">
                  {typeof premium === "number" ? `AED ${premium.toLocaleString()}` : premium}
                </div>
                <p className="text-sm text-gray-500">Quote Reference: {quoteNumber}</p>
              </div>

              <div className="border-t pt-6 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Business Type:</span>
                  <span className="font-semibold text-gray-900 capitalize">{formData?.segment || "N/A"}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Company:</span>
                  <span className="font-semibold text-gray-900">{formData?.companyName || "N/A"}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Coverage Start:</span>
                  <span className="font-semibold text-gray-900">{formData?.startDate || "N/A"}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* What's covered */}
          <Card className="mb-6">
            <CardContent className="p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">What's Covered</h3>
              <ul className="space-y-2">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700">Property damage (building, contents, equipment)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700">Business interruption protection</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700">Public liability coverage</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700">Employer's liability (workers' compensation)</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Next steps */}
          <Card className="mb-6 bg-blue-50 border-blue-200">
            <CardContent className="p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Next Steps</h3>
              <p className="text-sm text-gray-700 mb-4">
                Our insurance specialist will contact you within 24 hours to finalize your policy and answer any questions.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Call Us Now
                </Button>
                <Button variant="outline" className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Email Quote
                </Button>
                <Button variant="outline" className="flex items-center gap-2">
                  <Download className="w-4 h-4" />
                  Download PDF
                </Button>
              </div>
            </CardContent>
          </Card>

          <div className="text-center">
            <Button variant="ghost" onClick={() => navigate("/")}>
              Return to Homepage
            </Button>
          </div>
        </div>
      </div>
      <TenantFooter />
    </div>
  );
};

export default QuotePrice;

